import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Divider,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@pv/common/hooks';
import { Fields } from '@pv/common/components';
import { isEmpty } from 'lodash';
import { PaymentOptionsSelect } from '../../pages/Settings/Organization/components/PaymentOptionsSelect';
import { PlanOptionsSelect } from '../../pages/Settings/Organization/components/PlanOptionsSelect';
import { BillingChangeForm } from '../../pages/Settings/Organization/components/BillingChangeForm';
import {
  BillingAddressField,
  emptyBillingAddress,
  isCompleteBillingAddress,
  toBillingAddress,
  toBillingAddressInput,
} from '../../pages/Settings/Organization/components/BillingAddressField';
import type { AddressType as Address } from '@pv/common/components';
import type { AddressInput } from '@pv/common/graphql/types';
import size from 'lodash/size';
import { useUserAuth } from '../../providers/auth';
import { updateVenuePlanMutation, createVenueMutation } from './graphql';
import { usePlanSelection } from './hooks/usePlanSelection';
import { usePaymentProcessing } from './hooks/usePaymentProcessing';
import { usePricingCalculations } from './hooks/usePricingCalculations';

import { PlanChangeReasonField } from './components/PlanChangeReasonField';
import { TermsOfServiceLink } from './components/TermsOfServiceLink';
import type { UpgradeVenuePlanModalContentProps } from './types';
import { BasePlanPricingSummaryBox } from './components/BasePlanPricingSummaryBox';
export const UpgradeVenuePlanModalContent = ({
  venue,
  organization,
  availablePlans,
  paymentMethods,
  open,
  onClose,
  afterConfirm,
  isCreate = false,
  initialBillingInterval,
  initialTier,
}: UpgradeVenuePlanModalContentProps) => {
  const { user } = useUserAuth();

  const [forceValidate, setForceValidate] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [venueName, setVenueName] = useState<string>('');
  const [planChangeReason, setPlanChangeReason] = useState<string>('');
  const [billingAddress, setBillingAddress] =
    useState<Address>(emptyBillingAddress);
  // A new card goes onto the org's Stripe customer, which Stripe Tax won't
  // accept without an address on file.
  const needsBillingAddress =
    isEmpty(paymentMethods) &&
    !isCompleteBillingAddress(toBillingAddress(organization?.billingAddress));

  // Plan selection hook
  const { currentPlan, selectedPlan, setSelectedPlan } = usePlanSelection({
    availablePlans,
    venue: venue || null,
    initialTier,
    initialBillingInterval: initialBillingInterval,
  });

  // Payment processing hook
  const {
    billingEmail,
    setBillingEmail,
    stripeLoading,
    setStripeLoading,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    cardholderName,
    setCardholderName,
    getStripePaymentMethodToken,
    validateBillingInfo,
  } = usePaymentProcessing({
    venue: venue || null,
    paymentMethods,
    organizationBillingEmail: organization?.billingEmail || undefined,
    open,
    user: user || undefined,
  });

  // Pricing calculations hook
  const { monthlyRateWithDiscount, discount } =
    usePricingCalculations({
      selectedPlanMonthlyRate: selectedPlan?.monthlyRate || 0,
      selectedPlanBillingInterval: selectedPlan?.billingInterval || 'monthly',
      organizationDiscount: organization?.discount || 0,
    });

  // Initialize venue name and add-ons
  useEffect(() => {
    setVenueName(venue?.name || '');
  }, [venue]);

  const [updateVenuePlan, { loading: updateLoading }] = useMutation(
    updateVenuePlanMutation
  );

  const [createVenue, { loading: createLoading }] = useMutation(
    createVenueMutation,
    {
      onNoErrorsCompleted: () => {
        setVenueName('');
        if (afterConfirm) {
          afterConfirm();
        }
        setStripeLoading(false);
        onClose();
        enqueueSnackbar('Venue has been created', { variant: 'success' });
      },
    }
  );

  const onChangeVenueName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVenueName(e.target.value);
    setNameError(false);
  };

  interface OnConfirmInput {
    planDefinitionId: string;
    stripeTokenId?: string;
    billingEmail?: string;
    billingAddress?: AddressInput;
    organizationId?: string;
    name?: string;
    id?: string;
    note?: string;
    stripeSourceId?: string;
  }

  const onConfirm = async (): Promise<void> => {
    const input = {
      planDefinitionId:
        selectedPlan?.id || venue?.subscription?.planDefinitionId || '',
    } as OnConfirmInput;
    let stripeTokenId: string | undefined;

    if (needsBillingAddress && !isCompleteBillingAddress(billingAddress)) {
      setForceValidate(true);
      return;
    }

    if (isEmpty(paymentMethods)) {
      setStripeLoading(true);

      try {
        stripeTokenId = await getStripePaymentMethodToken();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setStripeLoading(false);
        return;
      }

      const validation = validateBillingInfo();
      if (!validation.valid) {
        setForceValidate(true);
        setStripeLoading(false);
        return;
      }

      setStripeLoading(false);
    }
    if (isCreate && isEmpty(venueName)) {
      setForceValidate(true);
      setNameError(true);
      setStripeLoading(false);
      return;
    }

    if (stripeTokenId) {
      input.stripeTokenId = stripeTokenId;
      input.billingEmail = billingEmail || undefined;
      if (needsBillingAddress) {
        input.billingAddress = toBillingAddressInput(billingAddress);
      }
    }

    if (isCreate) {
      if (!organization?.id) {
        enqueueSnackbar('Organization ID is required', { variant: 'error' });
        return;
      }
      input.organizationId = organization.id;
      input.name = venueName;
    } else {
      if (!venue?.id) {
        enqueueSnackbar('Venue ID is required', { variant: 'error' });
        return;
      }
      input.id = venue.id;
      input.note = planChangeReason;
    }
    if (selectedPaymentMethodId) {
      input.stripeSourceId = selectedPaymentMethodId;
    }
    const variables = { input };
    if (isCreate) {
      return createVenue({ variables });
    } else {
      setStripeLoading(true);
      // Update plan first, then handle add-on changes
      await updateVenuePlan({ variables });

      // Close modal and trigger refetch after all changes complete
      enqueueSnackbar('Updated Plan', { variant: 'success' });
      if (afterConfirm) {
        afterConfirm();
      }
      setStripeLoading(false);
      onClose();
    }
  };

  const loading = updateLoading || stripeLoading || createLoading;

  const monthlyRate = selectedPlan?.monthlyRate
    ? selectedPlan.monthlyRate / 100.0
    : 0.0;
  const includeStarter = !isCreate && size(organization?.venues) === 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        component: 'form',
        onSubmit: (e: React.FormEvent) => {
          e.preventDefault();
          onConfirm();
        },
      }}
      data-cy="upgrade-venue-plan-modal"
    >
      <DialogTitle>{isCreate ? 'Add a Venue' : 'Select a Plan'}</DialogTitle>
      <Divider />
      <DialogContent data-cy="upgrade-venue-plan-modal-content">
        <Grid container spacing={2}>
          {isCreate ? (
            <Grid item xs={12}>
              <Fields.PvTextField
                required
                forceValidate={forceValidate}
                name="venue-name"
                label="Venue Name"
                value={venueName}
                forceError={nameError}
                onChange={onChangeVenueName}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography variant="h5" style={{ marginBottom: '24px' }}>
                {venueName}
              </Typography>
            </Grid>
          )}

          <PlanOptionsSelect
            includeStarter={includeStarter}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            availablePlans={availablePlans}
            initialTier={initialTier}
            initialBillingInterval={initialBillingInterval}
          />

          {monthlyRate > 0 &&
            (!isEmpty(paymentMethods) ? (
              <Grid item xs={12}>
                <PaymentOptionsSelect
                  onSelect={setSelectedPaymentMethodId}
                  paymentMethods={paymentMethods}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <BillingChangeForm
                  billingEmail={billingEmail}
                  setBillingEmail={setBillingEmail}
                  forceValidate={forceValidate}
                  setForceValidate={setForceValidate}
                  cardholderName={cardholderName}
                  setCardholderName={setCardholderName}
                />
                {needsBillingAddress && (
                  <div style={{ marginTop: '16px' }}>
                    <BillingAddressField
                      value={billingAddress}
                      onChange={setBillingAddress}
                      forceValidate={forceValidate}
                    />
                  </div>
                )}
              </Grid>
            ))}

          {selectedPlan && (
            <Grid item xs={12}>
              <BasePlanPricingSummaryBox
                selectedPlan={selectedPlan}
                monthlyRateWithDiscount={monthlyRateWithDiscount}
                discount={discount}
              />
            </Grid>
          )}
          <PlanChangeReasonField
            planChangeReason={planChangeReason}
            setPlanChangeReason={setPlanChangeReason}
            show={!isCreate && selectedPlan?.tier !== currentPlan?.tier}
          />
          <TermsOfServiceLink />
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: '24px' }}>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          data-cy="modal-submit-button"
          type="submit"
          variant="contained"
          color="secondary"
          disabled={loading}
          onClick={onConfirm}
        >
          Save & Pay
        </Button>
      </DialogActions>
    </Dialog>
  );
};
